/**
 * Lot Management Page
 * 
 * AS9100D Clause: 8.5.2 Identification and Traceability
 * - Full lot lifecycle management
 * - Status transitions with audit trail
 * - FIFO compliance tracking
 * - Lot transaction history (forward/backward traceability)
 * 
 * Sprint 2: Inventory Core - Lot/Serial Tracking
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Package, 
  Plus, 
  Search, 
  Eye,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  ChevronRight,
  ChevronDown,
  RefreshCw,
  ArrowLeft,
  Shield,
  Loader2,
  MoreHorizontal,
  History,
  ArrowRightLeft,
  Boxes,
  FileCheck,
  Calendar,
  Building2,
  Truck,
  AlertCircle,
  Filter,
  Download
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import ComplianceTooltip from '../components/ComplianceTooltip';
import InventoryLayout from '../components/layouts/InventoryLayout';
import { formatIST } from '../utils/timezone';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status configuration with AS9100D clause references
const STATUS_CONFIG = {
  QC_HOLD: {
    label: 'QC Hold',
    color: 'bg-amber-100 text-amber-800 border-amber-300',
    icon: Clock,
    clause: '8.4.2',
    description: 'Awaiting quality inspection',
    transitions: ['RELEASED', 'QUARANTINE']
  },
  RELEASED: {
    label: 'Released',
    color: 'bg-green-100 text-green-800 border-green-300',
    icon: CheckCircle,
    clause: '8.6',
    description: 'Approved for use',
    transitions: ['QUARANTINE', 'CONSUMED', 'EXPIRED']
  },
  QUARANTINE: {
    label: 'Quarantine',
    color: 'bg-red-100 text-red-800 border-red-300',
    icon: AlertTriangle,
    clause: '8.7',
    description: 'Nonconforming material',
    transitions: ['RELEASED', 'CONSUMED']
  },
  EXPIRED: {
    label: 'Expired',
    color: 'bg-gray-100 text-gray-800 border-gray-300',
    icon: XCircle,
    clause: '8.5.4',
    description: 'Shelf life exceeded',
    transitions: ['QUARANTINE']
  },
  CONSUMED: {
    label: 'Consumed',
    color: 'bg-blue-100 text-blue-800 border-blue-300',
    icon: Boxes,
    clause: '8.5.1',
    description: 'Fully issued/used',
    transitions: []
  }
};

// Ownership types
const OWNERSHIP_TYPES = [
  { value: 'OWNED', label: 'Company Owned' },
  { value: 'CUSTOMER', label: 'Customer Property (8.5.3)' },
  { value: 'CONSIGNMENT', label: 'Consignment' }
];

/**
 * Status Badge Component
 */
const StatusBadge = ({ status }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.QC_HOLD;
  const Icon = config.icon;
  
  return (
    <Badge variant="outline" className={`${config.color} px-2 py-1 text-xs font-medium`}>
      <Icon className="w-3 h-3 mr-1" />
      {config.label}
    </Badge>
  );
};

/**
 * FIFO Indicator - Shows days since receipt
 */
const FIFOIndicator = ({ receiptDate }) => {
  if (!receiptDate) return null;
  
  const receipt = new Date(receiptDate);
  const today = new Date();
  const days = Math.floor((today - receipt) / (1000 * 60 * 60 * 24));
  
  let color = 'bg-green-100 text-green-700';
  if (days > 90) color = 'bg-red-100 text-red-700';
  else if (days > 30) color = 'bg-amber-100 text-amber-700';
  
  return (
    <ComplianceTooltip 
      field="lot_receipt_date"
      position="top"
    >
      <Badge variant="outline" className={`${color} text-xs cursor-help`}>
        FIFO: {days}d
      </Badge>
    </ComplianceTooltip>
  );
};

/**
 * Days to Expiry Indicator
 */
const ExpiryIndicator = ({ daysToExpiry, expiryDate }) => {
  if (daysToExpiry === null || daysToExpiry === undefined) return null;
  
  let color = 'bg-green-100 text-green-700';
  let icon = CheckCircle;
  
  if (daysToExpiry <= 0) {
    color = 'bg-red-100 text-red-700';
    icon = XCircle;
  } else if (daysToExpiry <= 30) {
    color = 'bg-amber-100 text-amber-700';
    icon = AlertTriangle;
  }
  
  const Icon = icon;
  
  return (
    <ComplianceTooltip
      field="lot_expiry_date"
      position="top"
    >
      <Badge variant="outline" className={`${color} text-xs cursor-help`}>
        <Icon className="w-3 h-3 mr-1" />
        {daysToExpiry <= 0 ? 'EXPIRED' : `${daysToExpiry}d left`}
      </Badge>
    </ComplianceTooltip>
  );
};

/**
 * Lot Detail Sheet - Right side panel showing full lot details
 */
const LotDetailSheet = ({ lot, isOpen, onClose, onStatusChange, onRefresh }) => {
  const [transactions, setTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusReason, setStatusReason] = useState('');
  const [updating, setUpdating] = useState(false);
  
  useEffect(() => {
    if (isOpen && lot) {
      loadTransactions();
    }
  }, [isOpen, lot?.lot_id]);
  
  const loadTransactions = async () => {
    if (!lot?.lot_id) return;
    
    setLoadingTransactions(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `${API_URL}/api/v1/inventory/v2/lots/${lot.lot_id}/transactions`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTransactions(response.data.transactions || []);
    } catch (error) {
      console.error('Error loading transactions:', error);
    } finally {
      setLoadingTransactions(false);
    }
  };
  
  const handleStatusChange = async () => {
    if (!newStatus) return;
    
    setUpdating(true);
    try {
      const token = localStorage.getItem('access_token');
      await axios.put(
        `${API_URL}/api/v1/inventory/v2/lots/${lot.lot_id}/status`,
        { new_status: newStatus, reason: statusReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Lot status changed to ${STATUS_CONFIG[newStatus]?.label || newStatus}`);
      setStatusDialogOpen(false);
      setNewStatus('');
      setStatusReason('');
      onRefresh && onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setUpdating(false);
    }
  };
  
  if (!lot) return null;
  
  const statusConfig = STATUS_CONFIG[lot.status] || STATUS_CONFIG.QC_HOLD;
  
  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-[600px] sm:max-w-[600px]">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Package className="w-5 h-5 text-[#101957]" />
            Lot: {lot.lot_number}
          </SheetTitle>
          <SheetDescription>
            AS9100D Clause 8.5.2 - Batch Traceability
          </SheetDescription>
        </SheetHeader>
        
        <ScrollArea className="h-[calc(100vh-120px)] pr-4">
          <div className="space-y-6 py-4">
            {/* Status Section */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium">Status</CardTitle>
                  {statusConfig.transitions.length > 0 && (
                    <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
                      <DialogTrigger asChild>
                        <Button variant="outline" size="sm">
                          <ArrowRightLeft className="w-3 h-3 mr-1" />
                          Change Status
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Change Lot Status</DialogTitle>
                          <DialogDescription>
                            AS9100D requires documented reason for status changes
                          </DialogDescription>
                        </DialogHeader>
                        
                        <div className="space-y-4 py-4">
                          <div className="space-y-2">
                            <Label>Current Status</Label>
                            <div className="flex items-center gap-2">
                              <StatusBadge status={lot.status} />
                              <span className="text-xs text-muted-foreground">
                                Clause {statusConfig.clause}
                              </span>
                            </div>
                          </div>
                          
                          <div className="space-y-2">
                            <Label>New Status</Label>
                            <Select value={newStatus} onValueChange={setNewStatus}>
                              <SelectTrigger>
                                <SelectValue placeholder="Select new status" />
                              </SelectTrigger>
                              <SelectContent>
                                {statusConfig.transitions.map(status => (
                                  <SelectItem key={status} value={status}>
                                    <div className="flex items-center gap-2">
                                      <StatusBadge status={status} />
                                      <span className="text-xs text-muted-foreground ml-2">
                                        ({STATUS_CONFIG[status]?.clause})
                                      </span>
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          
                          <div className="space-y-2">
                            <Label>Reason for Change *</Label>
                            <Textarea
                              value={statusReason}
                              onChange={(e) => setStatusReason(e.target.value)}
                              placeholder="Document reason for status change (required for AS9100D 10.2)"
                              rows={3}
                            />
                          </div>
                        </div>
                        
                        <DialogFooter>
                          <Button variant="outline" onClick={() => setStatusDialogOpen(false)}>
                            Cancel
                          </Button>
                          <Button 
                            onClick={handleStatusChange}
                            disabled={!newStatus || !statusReason || updating}
                            className="bg-[#101957] text-white hover:bg-[#101957]/90"
                          >
                            {updating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            Update Status
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4">
                  <StatusBadge status={lot.status} />
                  <FIFOIndicator receiptDate={lot.receipt_date} />
                  <ExpiryIndicator 
                    daysToExpiry={lot.days_to_expiry} 
                    expiryDate={lot.expiry_date}
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  {statusConfig.description} - AS9100D {statusConfig.clause}
                </p>
              </CardContent>
            </Card>
            
            {/* Part Information */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Boxes className="w-4 h-4" />
                  Part Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <Label className="text-xs text-muted-foreground">Part ID</Label>
                    <p className="font-medium">{lot.part_id}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Revision</Label>
                    <p className="font-medium">{lot.part_revision || 'A'}</p>
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs text-muted-foreground">Description</Label>
                    <p className="font-medium">{lot.part_description || lot.part_number || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">UOM</Label>
                    <p className="font-medium">{lot.uom || 'EA'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Quantities */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Package className="w-4 h-4" />
                  Quantities
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <Label className="text-xs text-blue-600">Received Qty</Label>
                    <p className="text-xl font-bold text-blue-700">{lot.received_quantity}</p>
                  </div>
                  <div className="p-3 bg-green-50 rounded-lg">
                    <Label className="text-xs text-green-600">Current Qty</Label>
                    <p className="text-xl font-bold text-green-700">{lot.current_quantity}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Supplier Information */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Truck className="w-4 h-4" />
                  Supplier Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <Label className="text-xs text-muted-foreground">Supplier Name</Label>
                    <p className="font-medium">{lot.supplier_name || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Supplier Lot #</Label>
                    <p className="font-medium">{lot.supplier_lot_number || '-'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Dates */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Important Dates
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <Label className="text-xs text-muted-foreground">Receipt Date</Label>
                    <p className="font-medium">
                      {lot.receipt_date ? formatIST(lot.receipt_date, { includeTime: false }) : '-'}
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Manufacture Date</Label>
                    <p className="font-medium">
                      {lot.manufacture_date ? formatIST(lot.manufacture_date, { includeTime: false }) : '-'}
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Expiry Date</Label>
                    <p className={`font-medium ${lot.days_to_expiry !== null && lot.days_to_expiry <= 0 ? 'text-red-600' : ''}`}>
                      {lot.expiry_date ? formatIST(lot.expiry_date, { includeTime: false }) : '-'}
                    </p>
                  </div>
                  {lot.released_at && (
                    <div>
                      <Label className="text-xs text-muted-foreground">Released Date</Label>
                      <p className="font-medium">
                        {formatIST(lot.released_at, { includeTime: false })}
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
            
            {/* Compliance Flags */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  AS9100D Compliance
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {lot.is_hazmat && (
                    <Badge variant="outline" className="bg-orange-100 text-orange-800 border-orange-300">
                      <AlertTriangle className="w-3 h-3 mr-1" />
                      HazMat (8.5.4)
                    </Badge>
                  )}
                  {lot.is_esd_sensitive && (
                    <Badge variant="outline" className="bg-yellow-100 text-yellow-800 border-yellow-300">
                      <AlertCircle className="w-3 h-3 mr-1" />
                      ESD Sensitive (8.5.4)
                    </Badge>
                  )}
                  {lot.coc_verified && (
                    <Badge variant="outline" className="bg-green-100 text-green-800 border-green-300">
                      <FileCheck className="w-3 h-3 mr-1" />
                      CoC Verified (8.1.4)
                    </Badge>
                  )}
                  {lot.ownership_type === 'CUSTOMER' && (
                    <Badge variant="outline" className="bg-purple-100 text-purple-800 border-purple-300">
                      <Building2 className="w-3 h-3 mr-1" />
                      Customer Property (8.5.3)
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>
            
            {/* Transaction History */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <History className="w-4 h-4" />
                    Transaction History
                  </CardTitle>
                  <Badge variant="secondary" className="text-xs">
                    {transactions.length} records
                  </Badge>
                </div>
                <CardDescription className="text-xs">
                  AS9100D 8.5.2 - Forward/backward traceability
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loadingTransactions ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : transactions.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <History className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No transactions recorded</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {transactions.map((txn, idx) => (
                      <div key={txn.transaction_id || idx} className="flex items-start gap-3 p-2 bg-muted/50 rounded-lg">
                        <div className="w-2 h-2 mt-2 rounded-full bg-[#101957]" />
                        <div className="flex-1 text-sm">
                          <p className="font-medium">{txn.transaction_type}</p>
                          <p className="text-xs text-muted-foreground">
                            Qty: {txn.quantity} | {formatIST(txn.performed_at)}
                          </p>
                          {txn.reason_code && (
                            <p className="text-xs text-muted-foreground">
                              Reason: {txn.reason_code}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};

/**
 * Create Lot Modal
 */
const CreateLotModal = ({ isOpen, onClose, onSuccess, parts }) => {
  const [formData, setFormData] = useState({
    part_id: '',
    lot_number: '',
    supplier_lot_number: '',
    supplier_name: '',
    manufacture_date: '',
    expiry_date: '',
    received_quantity: '',
    current_quantity: '',
    status: 'QC_HOLD',
    ownership_type: 'OWNED',
    customer_name: '',
    storage_requirements: ''
  });
  const [creating, setCreating] = useState(false);
  const [searchParts, setSearchParts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.part_id) {
      toast.error('Please select a part');
      return;
    }
    
    setCreating(true);
    try {
      const token = localStorage.getItem('access_token');
      
      const payload = {
        ...formData,
        received_quantity: parseFloat(formData.received_quantity) || 0,
        current_quantity: parseFloat(formData.current_quantity) || parseFloat(formData.received_quantity) || 0
      };
      
      // Remove empty strings
      Object.keys(payload).forEach(key => {
        if (payload[key] === '') delete payload[key];
      });
      
      await axios.post(
        `${API_URL}/api/v1/inventory/v2/lots`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Lot created successfully');
      onSuccess && onSuccess();
      onClose();
      
      // Reset form
      setFormData({
        part_id: '',
        lot_number: '',
        supplier_lot_number: '',
        supplier_name: '',
        manufacture_date: '',
        expiry_date: '',
        received_quantity: '',
        current_quantity: '',
        status: 'QC_HOLD',
        ownership_type: 'OWNED',
        customer_name: '',
        storage_requirements: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create lot');
    } finally {
      setCreating(false);
    }
  };
  
  // Search parts
  useEffect(() => {
    if (searchQuery.length >= 2) {
      const filtered = parts.filter(p => 
        p.hpn_compact?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.mpn?.toLowerCase().includes(searchQuery.toLowerCase())
      ).slice(0, 10);
      setSearchParts(filtered);
    } else {
      setSearchParts([]);
    }
  }, [searchQuery, parts]);
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-5 h-5 text-[#101957]" />
            Create New Lot
          </DialogTitle>
          <DialogDescription>
            AS9100D Clause 8.5.2 - Unique batch identification
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit}>
          <ScrollArea className="h-[60vh] pr-4">
            <div className="space-y-6 py-4">
              {/* Part Selection */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  Part *
                  <ComplianceTooltip field="lot_part_id" position="right" />
                </Label>
                <div className="relative">
                  <Input
                    placeholder="Search by HPN, MPN, or description..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className={formData.part_id ? 'border-green-500' : ''}
                  />
                  {formData.part_id && (
                    <Badge className="absolute right-2 top-2 bg-green-100 text-green-800">
                      {formData.part_id}
                    </Badge>
                  )}
                </div>
                {searchParts.length > 0 && (
                  <div className="border rounded-md max-h-48 overflow-auto">
                    {searchParts.map(part => (
                      <div
                        key={part.hpn_compact}
                        className="p-2 hover:bg-muted cursor-pointer border-b last:border-b-0"
                        onClick={() => {
                          setFormData(prev => ({ ...prev, part_id: part.hpn_compact }));
                          setSearchQuery(part.description || part.hpn_compact);
                          setSearchParts([]);
                        }}
                      >
                        <p className="font-medium text-sm">{part.hpn_compact}</p>
                        <p className="text-xs text-muted-foreground truncate">{part.description}</p>
                        {part.mpn && <p className="text-xs text-blue-600">MPN: {part.mpn}</p>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <Separator />
              
              {/* Lot Identification */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Lot Number (Auto-generated if blank)</Label>
                  <Input
                    value={formData.lot_number}
                    onChange={(e) => setFormData(prev => ({ ...prev, lot_number: e.target.value }))}
                    placeholder="LOT-YYYYMMDD-XXXX"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Supplier Lot Number</Label>
                  <Input
                    value={formData.supplier_lot_number}
                    onChange={(e) => setFormData(prev => ({ ...prev, supplier_lot_number: e.target.value }))}
                    placeholder="Manufacturer's batch code"
                  />
                </div>
              </div>
              
              {/* Supplier Info */}
              <div className="space-y-2">
                <Label>Supplier Name</Label>
                <Input
                  value={formData.supplier_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, supplier_name: e.target.value }))}
                  placeholder="Enter supplier name"
                />
              </div>
              
              <Separator />
              
              {/* Quantities */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    Received Quantity *
                    <ComplianceTooltip field="lot_received_qty" position="right" />
                  </Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.received_quantity}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      received_quantity: e.target.value,
                      current_quantity: e.target.value // Default current = received
                    }))}
                    placeholder="0"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Current Quantity</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.current_quantity}
                    onChange={(e) => setFormData(prev => ({ ...prev, current_quantity: e.target.value }))}
                    placeholder="Same as received"
                  />
                </div>
              </div>
              
              <Separator />
              
              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Manufacture Date</Label>
                  <Input
                    type="date"
                    value={formData.manufacture_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, manufacture_date: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    Expiry Date
                    <ComplianceTooltip field="lot_expiry_date" position="right" />
                  </Label>
                  <Input
                    type="date"
                    value={formData.expiry_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, expiry_date: e.target.value }))}
                  />
                </div>
              </div>
              
              <Separator />
              
              {/* Status & Ownership */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    Initial Status
                    <ComplianceTooltip field="lot_status" position="right" />
                  </Label>
                  <Select 
                    value={formData.status} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, status: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="QC_HOLD">QC Hold (8.4.2 default)</SelectItem>
                      <SelectItem value="RELEASED">Released (8.6)</SelectItem>
                      <SelectItem value="QUARANTINE">Quarantine (8.7)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    Ownership Type
                    <ComplianceTooltip field="lot_ownership" position="right" />
                  </Label>
                  <Select 
                    value={formData.ownership_type} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, ownership_type: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {OWNERSHIP_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {formData.ownership_type === 'CUSTOMER' && (
                <div className="space-y-2">
                  <Label>Customer Name (8.5.3)</Label>
                  <Input
                    value={formData.customer_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, customer_name: e.target.value }))}
                    placeholder="Customer providing the material"
                  />
                </div>
              )}
              
              {/* Storage Requirements */}
              <div className="space-y-2">
                <Label>Special Storage Requirements (8.5.4)</Label>
                <Textarea
                  value={formData.storage_requirements}
                  onChange={(e) => setFormData(prev => ({ ...prev, storage_requirements: e.target.value }))}
                  placeholder="Temperature, humidity, ESD, HazMat requirements..."
                  rows={2}
                />
              </div>
            </div>
          </ScrollArea>
          
          <DialogFooter className="mt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={creating || !formData.part_id}
              className="bg-[#101957] text-white hover:bg-[#101957]/90"
            >
              {creating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Create Lot
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Main Lot Management Page
 */
const LotManagementPage = () => {
  const navigate = useNavigate();
  const [lots, setLots] = useState([]);
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [showZeroQty, setShowZeroQty] = useState(false);
  
  // Selected lot for detail view
  const [selectedLot, setSelectedLot] = useState(null);
  const [detailSheetOpen, setDetailSheetOpen] = useState(false);
  
  // Create modal
  const [createModalOpen, setCreateModalOpen] = useState(false);
  
  // Load lots
  const loadLots = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
        include_zero_qty: showZeroQty.toString()
      });
      
      if (statusFilter && statusFilter !== 'ALL') params.append('status', statusFilter);
      
      const response = await axios.get(
        `${API_URL}/api/v1/inventory/v2/lots?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setLots(response.data.items || []);
      setTotalCount(response.data.total_count || 0);
    } catch (error) {
      console.error('Error loading lots:', error);
      toast.error('Failed to load lots');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, statusFilter, showZeroQty]);
  
  // Load parts for create modal
  const loadParts = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `${API_URL}/api/v1/db-explorer/collections/parts?page=1&page_size=500`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setParts(response.data.documents || []);
    } catch (error) {
      console.error('Error loading parts:', error);
    }
  };
  
  useEffect(() => {
    loadLots();
    loadParts();
  }, [loadLots]);
  
  // Filter lots by search query (client-side)
  const filteredLots = lots.filter(lot => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      lot.lot_number?.toLowerCase().includes(query) ||
      lot.part_id?.toLowerCase().includes(query) ||
      lot.supplier_lot_number?.toLowerCase().includes(query) ||
      lot.supplier_name?.toLowerCase().includes(query)
    );
  });
  
  const totalPages = Math.ceil(totalCount / pageSize);
  
  // Open lot detail
  const handleViewLot = (lot) => {
    setSelectedLot(lot);
    setDetailSheetOpen(true);
  };
  
  return (
    <InventoryLayout>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div className="flex items-center gap-4">
              <div>
                <h1 className="text-2xl font-bold text-[#101957] flex items-center gap-2">
                  <Package className="w-6 h-6" />
                  Lot Management
                </h1>
                <p className="text-sm text-muted-foreground flex items-center gap-2 mt-1">
                  <Shield className="w-4 h-4 text-green-600" />
                  AS9100D Clause 8.5.2: Identification and Traceability
                </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={loadLots}>
              <RefreshCw className="w-4 h-4 mr-1" />
              Refresh
            </Button>
            <Button 
              onClick={() => setCreateModalOpen(true)}
              className="bg-[#101957] text-white hover:bg-[#101957]/90"
            >
              <Plus className="w-4 h-4 mr-1" />
              New Lot
            </Button>
          </div>
        </div>
        
        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search lot #, part ID, supplier..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>
              
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All Statuses</SelectItem>
                  {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>
                      {config.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Button
                variant={showZeroQty ? "default" : "outline"}
                size="sm"
                onClick={() => setShowZeroQty(!showZeroQty)}
              >
                <Filter className="w-4 h-4 mr-1" />
                {showZeroQty ? 'Showing All' : 'Hide Zero Qty'}
              </Button>
              
              <Badge variant="secondary">
                {totalCount} lots
              </Badge>
            </div>
          </CardContent>
        </Card>
        
        {/* Lots Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-8 h-8 animate-spin text-[#101957]" />
              </div>
            ) : filteredLots.length === 0 ? (
              <div className="text-center py-16">
                <Package className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <h3 className="text-lg font-medium text-muted-foreground">No lots found</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Create your first lot to start tracking inventory batches
                </p>
                <Button 
                  className="mt-4 bg-[#101957] text-white hover:bg-[#101957]/90"
                  onClick={() => setCreateModalOpen(true)}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Create Lot
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Lot Number
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Part ID
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Current Qty
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        FIFO / Expiry
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Supplier
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredLots.map(lot => (
                      <tr 
                        key={lot.lot_id} 
                        className="hover:bg-muted/30 cursor-pointer transition-colors"
                        onClick={() => handleViewLot(lot)}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Package className="w-4 h-4 text-[#101957]" />
                            <span className="font-medium text-sm">{lot.lot_number}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <span className="text-sm font-medium">{lot.part_id}</span>
                            {lot.part_description && (
                              <p className="text-xs text-muted-foreground truncate max-w-[200px]">
                                {lot.part_description}
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge status={lot.status} />
                        </td>
                        <td className="px-4 py-3">
                          <span className={`font-medium ${lot.current_quantity === 0 ? 'text-red-500' : ''}`}>
                            {lot.current_quantity}
                          </span>
                          <span className="text-xs text-muted-foreground ml-1">
                            / {lot.received_quantity}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <FIFOIndicator receiptDate={lot.receipt_date} />
                            <ExpiryIndicator 
                              daysToExpiry={lot.days_to_expiry} 
                              expiryDate={lot.expiry_date}
                            />
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-muted-foreground">
                            {lot.supplier_name || '-'}
                          </span>
                          {lot.supplier_lot_number && (
                            <p className="text-xs text-blue-600">
                              {lot.supplier_lot_number}
                            </p>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                              <Button variant="ghost" size="sm">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={(e) => {
                                e.stopPropagation();
                                handleViewLot(lot);
                              }}>
                                <Eye className="w-4 h-4 mr-2" />
                                View Details
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/inventory/stock?lot=${lot.lot_id}`);
                              }}>
                                <Boxes className="w-4 h-4 mr-2" />
                                View Balances
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={(e) => {
                                e.stopPropagation();
                                handleViewLot(lot);
                              }}>
                                <History className="w-4 h-4 mr-2" />
                                Transaction History
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t">
                <p className="text-sm text-muted-foreground">
                  Page {page} of {totalPages}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage(p => p - 1)}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= totalPages}
                    onClick={() => setPage(p => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* Detail Sheet */}
      <LotDetailSheet
        lot={selectedLot}
        isOpen={detailSheetOpen}
        onClose={() => setDetailSheetOpen(false)}
        onRefresh={loadLots}
      />
      
      {/* Create Modal */}
      <CreateLotModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={loadLots}
        parts={parts}
      />
      </div>
    </InventoryLayout>
  );
};

export default LotManagementPage;
